import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ExternalLink, HeartHandshake, Loader2, Search } from "lucide-react";
import { toast } from "sonner";

const tc = (s) => (s || "").toLowerCase().replace(/(^|\s|')\S/g, (m) => m.toUpperCase());

// Ricerca degli Enti del Terzo Settore nell'elenco ufficiale RUNTS (Ministero del Lavoro, CC BY 4.0).
export function RuntsCerca({ onScegli, label = "Cerca ETS nel RUNTS (per nome o codice fiscale)" }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [prov, setProv] = useState("");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const cerca = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try { const { data } = await api.get("/runts/cerca", { params: { q, provincia: prov } }); setRes(data); }
    catch (er) { toast.error(formatApiErrorDetail(er.response?.data?.detail)); } finally { setBusy(false); }
  };
  const scegli = (e) => { onScegli({ ...e, comune_tc: tc(e.comune), sezione_tc: tc(e.sezione) }); setOpen(false); toast.success(`Compilato dal RUNTS: ${e.denominazione}`); };
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className="inline-flex items-center gap-1.5 text-xs font-medium text-teal-700 hover:text-teal-900" data-testid="runts-apri">
        <HeartHandshake className="h-3.5 w-3.5" /> {label}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="runts-dialog">
          <DialogHeader><DialogTitle>Enti del Terzo Settore iscritti al RUNTS</DialogTitle></DialogHeader>
          <form onSubmit={cerca} className="flex gap-2">
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nome dell'ente o codice fiscale" data-testid="runts-q" autoFocus />
            <Input value={prov} onChange={(e) => setProv(e.target.value.toUpperCase().slice(0, 2))} placeholder="Prov." className="w-20" data-testid="runts-prov" />
            <Button type="submit" disabled={busy || q.trim().length < 3} data-testid="runts-cerca">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}</Button>
          </form>
          {res && !res.risultati.length && <p className="text-sm text-slate-500" data-testid="runts-vuoto">Nessun ente trovato. Provi con meno parole o senza provincia.</p>}
          <div className="divide-y">
            {res?.risultati.map((e) => (
              <div key={e.codice_fiscale} className="py-2.5 flex items-start gap-3" data-testid={`runts-ris-${e.codice_fiscale}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-900">{e.denominazione}</div>
                  <div className="text-xs text-slate-500">CF {e.codice_fiscale} · {tc(e.sezione)} · {tc(e.comune)} ({e.provincia}) · leg. rapp. {tc(e.legale_rappresentante)} · iscritto dal {e.data_iscrizione}</div>
                  <div className="flex gap-3 mt-1 text-[11px]">
                    <a href={e.link_runts} target="_blank" rel="noreferrer" className="text-slate-500 hover:text-slate-800 inline-flex items-center gap-0.5" data-testid={`runts-link-${e.codice_fiscale}`}>Apri su RUNTS <ExternalLink className="h-3 w-3" /></a>
                    <a href={e.link_italianonprofit} target="_blank" rel="noreferrer" className="text-slate-500 hover:text-slate-800 inline-flex items-center gap-0.5" data-testid={`runts-inp-${e.codice_fiscale}`}>Cerca su Italia non profit <ExternalLink className="h-3 w-3" /></a>
                  </div>
                </div>
                <Button size="sm" variant="outline" onClick={() => scegli(e)} data-testid={`runts-scegli-${e.codice_fiscale}`}>Usa</Button>
              </div>
            ))}
          </div>
          {res?.altri && <p className="text-xs text-slate-500">Mostrati i primi 50 risultati: affini la ricerca.</p>}
          <p className="text-[11px] text-slate-400">Fonte: Ministero del Lavoro e delle Politiche Sociali – RUNTS, dati aperti (CC BY 4.0), aggiornamento manuale a cura dell'amministratore.</p>
        </DialogContent>
      </Dialog>
    </>
  );
}
